import {
    Breadcrumb,
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, PackageCheck, Receipt, ShieldCheck, ShieldX, Truck } from 'lucide-react';
import { useGetList, useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { useHasPermission } from '../rbac/useHasPermission';
import { GoodsReceipt } from '../types';
import { GRNItemsList } from './GRNItemsList';


const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/goods_receipts">Goods Receipts</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>{record.reference}</Breadcrumb.PageItem>
            </Breadcrumb>
            <span className="text-xl font-bold tracking-tight text-foreground">{record.reference}</span>
        </>
    );
};

const GRNShowActions = () => {
    const record = useRecordContext<GoodsReceipt>();
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    const [update, { isPending }] = useUpdate();
    const { hasPermission: canManage } = useHasPermission('manage_procurement');

    // Gate: a GRN should be billed once. Disable "Create Vendor Bill" if a bill
    // already references it.
    const { data: existingBills } = useGetList(
        'vendor_bills',
        { filter: { grn_id: record?.id }, pagination: { page: 1, perPage: 1 } },
        { enabled: !!record?.id },
    );
    const hasBill = (existingBills?.length ?? 0) > 0;

    if (!record) return null;

    const handleStatusChange = (newStatus: string, label: string) => {
        update(
            'goods_receipts',
            { id: record.id, data: { status: newStatus }, previousData: record },
            {
                onSuccess: () => {
                    notify(`GRN ${label} successfully`, { type: 'success' });
                    refresh();
                },
                onError: () => notify('Error updating GRN status', { type: 'error' }),
            }
        );
    };

    const handleCreateVendorBill = () => {
        if (hasBill) return;
        navigate('/vendor_bills/create', {
            state: {
                record: {
                    grn_id: record.id,
                    po_id: record.po_id,
                    vendor_id: record.vendor_id,
                }
            }
        });
    };

    return (
        <div className="flex items-center gap-2">
            <EditButton />
            {canManage && (record.status === 'received' || record.status === 'qa_passed' || hasBill) && (
                <span title={hasBill ? 'A vendor bill has already been created from this GRN' : undefined}>
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        onClick={handleCreateVendorBill}
                        disabled={hasBill}
                    >
                        <Receipt className="w-4 h-4 mr-2" />
                        {hasBill ? 'Vendor Bill Created' : 'Create Vendor Bill'}
                    </Button>
                </span>
            )}
            {canManage && record.status === 'draft' && (
                <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => handleStatusChange('received', 'confirmed as received')}
                    disabled={isPending}
                >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirm Receipt
                </Button>
            )}
            {canManage && record.status === 'received' && (
                <>
                    <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleStatusChange('qa_passed', 'marked QA Passed')}
                        disabled={isPending}
                    >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Pass QA
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => handleStatusChange('qa_failed', 'marked QA Failed')}
                        disabled={isPending}
                    >
                        <ShieldX className="w-4 h-4 mr-2" />
                        Fail QA
                    </Button>
                </>
            )}
        </div>
    );
};

const GRNShowContent = () => {
    const record = useRecordContext<GoodsReceipt>();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            <div className="flex items-center gap-4 text-sm text-muted-foreground border-b pb-4 flex-wrap">
                <StatusBadge status={record.status} />
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-1 font-medium text-muted-foreground">
                    <Truck className="w-4 h-4 text-muted-foreground" /> Received: <DateField source="received_date" />
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                            Source Document
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-muted-foreground uppercase">Purchase Order</span>
                            <div>
                                <ReferenceField source="po_id" reference="purchase_orders" link="show">
                                    <TextField source="reference" className="text-blue-600 font-bold" />
                                </ReferenceField>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-muted-foreground uppercase">Vendor</span>
                            <div>
                                <ReferenceField source="vendor_id" reference="vendors" link="show">
                                    <TextField source="name" className="text-foreground font-medium" />
                                </ReferenceField>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                            Notes
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{record.notes || <span className="italic text-muted-foreground">No notes provided.</span>}</p>
                    </CardContent>
                </Card>
            </div>
            
            <GRNItemsList />
        </div>
    );
};

export const GRNShow = () => (
    <Show title={<ShowTitle />} actions={<GRNShowActions />} disableBreadcrumb>
        <GRNShowContent />
    </Show>
);
